import { Injectable } from '@angular/core';
import { Store, createStore } from '@ngneat/elf';
import {
  deleteEntities,
  hasEntity,
  removeActiveIds,
  resetActiveIds,
  selectActiveEntity,
  selectAllEntities,
  selectEntity,
  setActiveId,
  setEntities,
  upsertEntities,
  withActiveId,
  withEntities
} from '@ngneat/elf-entities';
import { BaseService } from '../services/base.service';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import {
  distinctUntilChanged,
  finalize,
  map,
  switchMap,
  take,
  tap
} from 'rxjs/operators';
import { Params } from '@angular/router';
import { PaginationData } from '@ngneat/elf-pagination';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { ElfUtil } from '@tcc-mono/shared/utils';
import { Import } from '@tcc-mono/classifact/shared/data-access/models';

@Injectable({
  providedIn: 'root'
})
export abstract class CoreRepository<TEntity extends { id: PropertyKey }> {
  protected _store: Store;

  protected _params = new BehaviorSubject<Params>({});
  public params$: Observable<Params> = this._params.asObservable();

  protected _loading = new BehaviorSubject<boolean>(false);
  public loading$: Observable<boolean> = this._loading.asObservable();

  protected _pagination = new BehaviorSubject<PaginationData<number>>(null);
  public pagination$: Observable<PaginationData<number>> = this._pagination.asObservable();

  public entities$: Observable<Array<TEntity>>;
  public activeEntity$: Observable<TEntity>;

  public canLoadMore$: Observable<boolean> = this.pagination$.pipe(
  map(p => !!p && p.currentPage < p.lastPage)
);

  constructor(
    protected readonly storeData: { name: string },
    protected readonly _service: BaseService<TEntity>
  ) {
    this._store = createStore(
      { name: this.storeData?.name },
      withEntities<TEntity>(),
      withActiveId()
    );

    this.entities$ = this._store.pipe(selectAllEntities());
    this.activeEntity$ = this._store.pipe(selectActiveEntity());
  }

  public listenToParams(): Observable<unknown> {
    return this.params$.pipe(
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      map((params: Params) => ({ ...params, page: 1 })),
      switchMap((params: Params) => this._getEntities(params)),
      tap((entities: TEntity[]) => this._store.update(setEntities(entities)))
    );
  }

  public scrollLoad(event?: any): void {
  const pagination = this._pagination?.value;

  if (!pagination || pagination.currentPage >= pagination.lastPage) {
    return; // Geen verdere pagina's → niks doen
  }

  const nextPage = (pagination?.currentPage ?? 0) + 1;
  const params = {
    ...this._params.value,
    page: nextPage
  };

  this._loading.next(true);

  this._getEntities(params)
    .pipe(
      take(1),
      tap((entities: TEntity[]) => this._store.update(upsertEntities(entities))),
      finalize(() => this._loading.next(false))
    )
    .subscribe();
}

  

  public resetParams(params: Params = {}): void {
    this._params.next(params);
  }

  public updateParams(params: Params): void {
    const newParams = {
      ...this._params.value,
      page: 1,
      ...params
    };
    this._params.next(newParams);
  }

  public refreshPage(page = 1): Observable<TEntity[]> {
    return this._getEntities({ ...this._params.value, page }).pipe(
      tap((entities: TEntity[]) => this._store.update(setEntities(entities)))
    );
  }

  public selectEntity(entityId: PropertyKey): Observable<TEntity> {
    return this._store.pipe(selectEntity(entityId));
  }

  public addEntity(entity: Partial<TEntity>): Observable<TEntity> {
    this._loading.next(true);
    return this._service.post(entity).pipe(
      finalize(() => this._loading.next(false)),
      map(({ data }) => data),
      tap((entity) => this._store.update(upsertEntities(entity))),
      tap((entity) => this.setActiveEntity(entity.id))
    );
  }

  public updateEntity(entityId: PropertyKey, entity: TEntity): Observable<TEntity> {
    this._loading.next(true);
    return this._service.put(entityId.toString(), entity).pipe(
      finalize(() => this._loading.next(false)),
      map(({ data }) => data),
      tap((entity) => this._store.update(upsertEntities(entity)))
    );
  }

  public getEntity(
    entityId: PropertyKey,
    setActive = true,
    forceGet = false,
    params: Params = {}
  ): Observable<TEntity> {
    if (setActive) this.setActiveEntity(entityId);

    const call = this._service.get(entityId.toString(), params).pipe(
      map(({ data }) => data),
      tap((entity) => this._store.update(upsertEntities(entity)))
    );

    return forceGet || !this._store.query(hasEntity(entityId)) ? call : EMPTY;
  }

  public removeEntity(entityId: PropertyKey): Observable<unknown> {
    this._loading.next(true);
    return this._service.delete(entityId.toString()).pipe(
      tap(() => this._store.update(deleteEntities(entityId))),
      finalize(() => this._loading.next(false))
    );
  }

  public addEntitiesInStore(entities: TEntity[]): void {
    this._store.update(upsertEntities(entities));
  }

  public setActiveEntity(entityId?: PropertyKey): void {
    this._store.update(setActiveId(entityId ?? []));
  }

  public hasEntity(entityId: PropertyKey): boolean {
    return this._store.query(hasEntity(entityId));
  }

  protected _updateEntities(data: Import[], p0: { merge: boolean; }, updated: TEntity[]): void {
    this._store.update(setEntities(updated));
  }

  private _getEntities(params: Params): Observable<TEntity[]> {
    this._loading.next(true);
    return this._service.getAll(params).pipe(
      finalize(() => this._loading.next(false)),
      tap((result) => {
        if (result.meta) {
          this._pagination.next(ElfUtil.convertMetaToElfPagination(result.meta));
        }
      }),
      map(({ data }) => data)
    );
  }
}
